import { Bot, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, formatCount, formatRelative } from "../api";
import { providerLabel, type ProviderId } from "../brands";
import { Button, EmptyState, ErrorState, LoadingState } from "../components";
import { diffLines, summarizeDiff, type DiffLine } from "../../shared/textDiff";
import { initialsFor } from "./loopNotes";

export type DefinitionModel = {
  id: string;
  loopId: string;
  version: number;
  body: string;
  format: "markdown" | "text";
  provider: ProviderId | null;
  changeNote: string | null;
  createdAt: string;
  author: { type: "user" | "agent"; id: string; name: string };
};
export type DefinitionInput = { body: string; changeNote?: string };

export type LoopDefinitionsState = {
  /** Newest first, as the API returns them. */
  definitions: DefinitionModel[];
  loading: boolean;
  error: string;
  record: (input: DefinitionInput) => Promise<DefinitionModel>;
  retry: () => void;
};

const BODY_MAX = 20_000;
const CHANGE_NOTE_MAX = 300;
/** Bodies longer than this many lines start collapsed. */
const COLLAPSE_LINES = 24;

/** Loads the definition history for one loop and exposes a recorder that prepends the returned version. */
export function useLoopDefinitions(organizationId: string | undefined, loopId: string): LoopDefinitionsState {
  const [definitions, setDefinitions] = useState<DefinitionModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!organizationId || !loopId) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void api<{ definitions: DefinitionModel[] }>(`/api/app/loops/${loopId}/definitions`, { signal: controller.signal }, organizationId)
      .then((data) => setDefinitions(data.definitions))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setError(caught instanceof Error ? caught.message : "The definition is unavailable.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [organizationId, loopId, refreshKey]);

  const record = useCallback(async (input: DefinitionInput) => {
    const { definition } = await api<{ definition: DefinitionModel }>(`/api/app/loops/${loopId}/definitions`, { method: "POST", body: JSON.stringify(input) }, organizationId);
    setDefinitions((current) => [definition, ...current.filter((existing) => existing.id !== definition.id)]);
    return definition;
  }, [organizationId, loopId]);

  const retry = useCallback(() => setRefreshKey((value) => value + 1), []);

  return { definitions, loading, error, record, retry };
}

function formatAbsolute(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function lineCount(body: string): number {
  return body.length === 0 ? 0 : body.split("\n").length;
}

/** The latest body in a monospace block; long bodies start folded with the line count and a "Show all" control. */
function DefinitionBody({ definition }: { definition: DefinitionModel }) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(false), [definition.id]);
  const lines = definition.body.split("\n");
  const foldable = lines.length > COLLAPSE_LINES;
  const shown = foldable && !expanded ? lines.slice(0, COLLAPSE_LINES).join("\n") : definition.body;
  return (
    <div className="loop-definition-body">
      <pre className={foldable && !expanded ? "is-folded" : ""} aria-label={`Definition v${definition.version}`}>{shown}</pre>
      <div className="loop-definition-body__foot">
        <small>{foldable && !expanded ? `${COLLAPSE_LINES} of ${lines.length} lines` : `${formatCount(lines.length, "line")} · ${definition.format}`}</small>
        {foldable ? (
          <Button variant="ghost" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
            {expanded ? <><ChevronUp size={14} aria-hidden="true" /> Show less</> : <><ChevronDown size={14} aria-hidden="true" /> Show all</>}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function DiffView({ definition, predecessor, diff }: { definition: DefinitionModel; predecessor: DefinitionModel | null; diff: DiffLine[] }) {
  const marks: Record<DiffLine["kind"], string> = { same: " ", added: "+", removed: "−" };
  return (
    <div className="loop-definition-diff" aria-label={`Changes in v${definition.version}`}>
      <header>
        <span>{predecessor ? `v${definition.version} against v${predecessor.version}` : "First version"}</span>
        <span>{summarizeDiff(diff)}</span>
      </header>
      <ol>
        {diff.map((line, index) => <li key={index} className={`is-${line.kind}`} data-mark={marks[line.kind]}>{line.text || " "}</li>)}
      </ol>
    </div>
  );
}

function VersionRow({ definition, predecessor, summary, selected, onSelect }: { definition: DefinitionModel; predecessor: DefinitionModel | null; summary: string; selected: boolean; onSelect: () => void }) {
  const diff = useMemo(() => (selected ? diffLines(predecessor?.body ?? "", definition.body) : []), [selected, predecessor, definition]);
  return (
    <li className={`loop-definition-version ${selected ? "is-selected" : ""}`.trim()}>
      <button type="button" aria-expanded={selected} onClick={onSelect}>
        <span className={`loop-definition-avatar loop-definition-avatar--${definition.author.type}`} aria-hidden="true">{definition.author.type === "agent" ? <Bot size={14} /> : initialsFor(definition.author.name)}</span>
        <span className="loop-definition-version__main">
          <span className="loop-definition-version__who">
            <strong>v{definition.version}</strong>
            <span>{definition.author.name}</span>
            {definition.author.type === "agent" ? <small>agent</small> : null}
            {definition.provider ? <small>for {providerLabel(definition.provider)}</small> : null}
          </span>
          <em>{definition.changeNote || "No change note"}</em>
        </span>
        <span className="loop-definition-version__trail">
          <b>{summary}</b>
          <time dateTime={definition.createdAt} title={formatAbsolute(definition.createdAt)}>{formatRelative(definition.createdAt)}</time>
        </span>
      </button>
      {selected ? <DiffView definition={definition} predecessor={predecessor} diff={diff} /> : null}
    </li>
  );
}

function Composer({ latest, onRecord }: { latest: DefinitionModel | null; onRecord: (input: DefinitionInput) => Promise<unknown> }) {
  const [body, setBody] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const trimmed = body.replace(/\r\n?/gu, "\n").trim();
  const unchanged = latest != null && trimmed === latest.body;
  const canRecord = trimmed.length > 0 && trimmed.length <= BODY_MAX && !unchanged && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canRecord) return;
    setBusy(true);
    setFormError("");
    try {
      await onRecord({ body: trimmed, changeNote: changeNote.trim() || undefined });
      setBody("");
      setChangeNote("");
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : "The version could not be recorded.");
    } finally {
      setBusy(false);
    }
  }

  function startFromLatest() {
    if (!latest) return;
    setBody(latest.body);
    textareaRef.current?.focus();
  }

  const hint = trimmed.length === 0
    ? latest ? `Next is v${latest.version + 1}` : "Records v1"
    : unchanged ? `Same as v${latest!.version}` : `${trimmed.length.toLocaleString()} / ${BODY_MAX.toLocaleString()}`;

  return (
    <form className="loop-definition-composer" onSubmit={submit}>
      <div className="loop-definition-composer__head">
        <span>{latest ? "Next version" : "First version"}</span>
        {latest ? <Button variant="ghost" type="button" onClick={startFromLatest} disabled={busy}>Start from v{latest.version}</Button> : null}
      </div>
      <label>
        <span className="sr-only">Definition body</span>
        <textarea ref={textareaRef} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Paste what the loop runs from: its instructions, schedule, the tools it may use, what it must never do." rows={7} maxLength={BODY_MAX} disabled={busy} spellCheck={false} />
      </label>
      <label className="loop-definition-composer__note">
        <span>Change note</span>
        <input value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="What changed and why" maxLength={CHANGE_NOTE_MAX} disabled={busy} />
      </label>
      {formError ? <div className="form-error" role="alert">{formError}</div> : null}
      <div className="loop-definition-composer__actions">
        <small>{hint}</small>
        <Button type="submit" busy={busy} disabled={!canRecord}>Record version</Button>
      </div>
    </form>
  );
}

/**
 * The Definition panel on loop detail: the latest body, the version history with a diff against
 * each predecessor, and the composer. Viewers of a shared loop read the history but cannot record.
 * `embedded` drops the panel chrome and head so the page's own section row names it.
 */
export function DefinitionPanel({ state, readOnly, originName, embedded = false }: { state: LoopDefinitionsState; readOnly: boolean; originName: string; embedded?: boolean }) {
  const { definitions, loading, error, record, retry } = state;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const latest = definitions[0] ?? null;

  const summaries = useMemo(() => {
    const byId = new Map<string, string>();
    definitions.forEach((definition, index) => {
      const predecessor = definitions[index + 1] ?? null;
      byId.set(definition.id, summarizeDiff(diffLines(predecessor?.body ?? "", definition.body)));
    });
    return byId;
  }, [definitions]);

  const Wrapper = embedded ? "div" : "section";
  return (
    <Wrapper className={embedded ? "loop-definition-panel loop-definition-panel--embedded" : "panel cockpit-panel loop-definition-panel"} aria-label={embedded ? undefined : "Loop definition"}>
      {!embedded ? (
        <div className="panel__head">
          <div><p className="eyebrow">Never scanned</p><h2>Definition</h2></div>
          <span className="loop-definition-meta">{latest ? <><b>v{latest.version}</b> · {latest.author.name} · <time dateTime={latest.createdAt} title={formatAbsolute(latest.createdAt)}>{formatRelative(latest.createdAt)}</time></> : "Not recorded"}</span>
        </div>
      ) : null}
      {loading ? (
        <LoadingState>Reading the definition…</LoadingState>
      ) : error ? (
        <ErrorState action={<Button variant="secondary" onClick={retry}>Try again</Button>}>Definition unavailable: {error}</ErrorState>
      ) : latest ? (
        <>
          <DefinitionBody definition={latest} />
          <div className="loop-definition-history__head"><FileText size={12} aria-hidden="true" /> {formatCount(definitions.length, "version")}{definitions.length >= 50 ? " · last 50 shown" : ""}</div>
          <ol className="loop-definition-history">
            {definitions.map((definition, index) => (
              <VersionRow
                key={definition.id}
                definition={definition}
                predecessor={definitions[index + 1] ?? null}
                summary={summaries.get(definition.id) ?? ""}
                selected={selectedId === definition.id}
                onSelect={() => setSelectedId((current) => (current === definition.id ? null : definition.id))}
              />
            ))}
          </ol>
        </>
      ) : (
        <EmptyState eyebrow="Not recorded" title="No definition recorded yet">
          {readOnly
            ? `${originName} has not recorded what this loop runs from. The scan prompt never sends a definition.`
            : "Paste what the loop runs from below, or ask its agent to call record_loop_definition. The scan prompt never sends a definition; it is recorded only when you or your agent choose to."}
        </EmptyState>
      )}
      {!loading && !error ? (
        readOnly ? (
          <p className="loop-definition-readonly">Recorded in {originName}. Only that workspace can record a version.</p>
        ) : (
          <Composer latest={latest} onRecord={record} />
        )
      ) : null}
    </Wrapper>
  );
}
